import { sanitizeStyleId } from '../beerStyle/sanitizeStyleId';
import { normalizeState } from './normalizeState';
import { normalizeAbv } from './normalizeAbv';
import { validateStyleType } from './validateStyleType';

/**
 * Resolves styleLabel to a catalog style id using mapping, catalog, or auto-create inputs.
 */
export function resolveStyleForRow(row, styleMapping, beerStyleIds, allowedTypeNames) {
  const styleLabel = row.styleLabel?.trim() ?? '';
  if (!styleLabel) {
    return { styleId: null, styleName: null, styleType: null, error: 'Style label is required' };
  }

  const mappedKey = styleMapping?.[styleLabel];
  if (mappedKey) {
    return { styleId: mappedKey, styleName: null, styleType: null, error: null };
  }

  const sanitizedId = sanitizeStyleId(styleLabel);
  if (sanitizedId && beerStyleIds?.has(sanitizedId)) {
    return { styleId: sanitizedId, styleName: null, styleType: null, error: null };
  }

  const styleTypeResult = validateStyleType(row.styleType, allowedTypeNames);
  if (styleTypeResult.error) {
    return {
      styleId: null,
      styleName: null,
      styleType: null,
      error: styleTypeResult.error,
    };
  }

  if (!sanitizedId) {
    return { styleId: null, styleName: null, styleType: null, error: 'Style label is invalid' };
  }

  return {
    styleId: sanitizedId,
    styleName: styleLabel,
    styleType: styleTypeResult.styleType,
    error: null,
  };
}

/**
 * Validates a parsed sheet row and returns normalized fields plus any errors.
 */
export function validateSheetRow(row, styleMapping, beerStyleIds, allowedTypeNames) {
  const errors = [];

  if (!row.breweryName?.trim()) {
    errors.push('Brewery name is required');
  }
  if (!row.beerName?.trim()) {
    errors.push('Beer name is required');
  }

  const stateResult = normalizeState(row.state);
  if (stateResult.error) {
    errors.push(stateResult.error);
  }

  const abvResult = normalizeAbv(row.abv);
  if (abvResult.error) {
    errors.push(abvResult.error);
  }

  const styleResult = resolveStyleForRow(row, styleMapping, beerStyleIds, allowedTypeNames);
  if (styleResult.error) {
    errors.push(styleResult.error);
  }

  return {
    rowIndex: row.rowIndex,
    breweryName: row.breweryName?.trim() ?? '',
    state: row.state?.trim() ?? '',
    stateCode: stateResult.code,
    beerName: row.beerName?.trim() ?? '',
    styleLabel: row.styleLabel?.trim() ?? '',
    abv: abvResult.abv,
    styleType: styleResult.styleType ?? row.styleType?.trim() ?? '',
    styleId: styleResult.styleId,
    styleName: styleResult.styleName,
    errors,
    isValid: errors.length === 0,
  };
}

/**
 * Validates all parsed rows.
 */
export function validateSheetRows(rows, styleMapping, beerStyleIds, allowedTypeNames) {
  const styleIdSet =
    beerStyleIds instanceof Set
      ? beerStyleIds
      : new Set(Array.isArray(beerStyleIds) ? beerStyleIds : []);

  return rows.map((row) => validateSheetRow(row, styleMapping, styleIdSet, allowedTypeNames));
}

/**
 * Builds a stable key for brewery grouping.
 */
export function breweryGroupKey(breweryName, stateCode) {
  return `${stateCode ?? ''}::${breweryName?.trim().toLowerCase() ?? ''}`;
}

/**
 * Returns unique brewery groups from validated rows (including invalid-state rows for display).
 */
export function getBreweryGroups(validatedRows) {
  const groups = new Map();

  validatedRows.forEach((row) => {
    const key = breweryGroupKey(row.breweryName, row.stateCode ?? row.state);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        breweryName: row.breweryName,
        stateCode: row.stateCode,
        stateRaw: row.state ?? '',
        rowIndices: [],
        hasStateError: false,
      });
    }
    const group = groups.get(key);
    group.rowIndices.push(row.rowIndex);
    if (!row.stateCode) {
      group.hasStateError = true;
    }
  });

  return Array.from(groups.values());
}
